import { createSlice } from '@reduxjs/toolkit';
import sortBy from 'lodash/sortBy';

import { FAUX_MEDICAL_EQUIPMENT_PSR } from 'config/app-config';

export const initialState = {
  isLoading: false,
  errors: null,
  data: {},
  psrPreferences: {},
  hasPsr: null
};

export const slice = createSlice({
  name: 'psr',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.isLoading = initialState.isLoading;
      state.errors = initialState.errors;
      state.data = initialState.data;
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload;
      state.errors = initialState.errors;
      Object.keys(payload).forEach(
        (psrType) => (state.psrPreferences[psrType] = [])
      );
    },
    setError: (state, { payload }) => {
      state.isLoading = initialState.isLoading;
      state.data = initialState.data;
      state.errors = payload;
    },
    setHasPsr: (state, { payload = false }) => {
      state.hasPsr = payload;
    },
    setPsrPreferences: (state, { payload }) => {
      const { psrItem, checked, type } = payload;

      if (checked) {
        state.psrPreferences[type] = [...state.psrPreferences[type], psrItem];
      } else {
        state.psrPreferences[type] = state.psrPreferences[type].filter(
          (psrOption) => {
            const toBeRemoved =
              psrOption.code === psrItem.code &&
              psrOption.subCode === psrItem.subCode;

            return !toBeRemoved;
          }
        );
      }
    },
    setPsrExpiryDate: (state, { payload }) => {
      const { code, subCode, expiryDate, type } = payload;
      const index = state.psrPreferences[type].findIndex(
        (item) => item.code === code && item.subCode === subCode
      );

      state.psrPreferences[type][index] = {
        ...state.psrPreferences[type][index],
        expiryDate
      };
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setSuccess,
  setError,
  setHasPsr,
  setPsrPreferences,
  setPsrExpiryDate
} = slice.actions;

export const selectSlice = (state) => state[slice.name];
export const selectData = (state) => selectSlice(state).data;
export const selectCombinedPsr = (state) => {
  const psr = selectData(state);
  const sortByDisplayName = (arr = []) => sortBy(arr, (o) => o.displayName);
  const combinedPsr = Object.entries(psr).reduce((acc, [key, value]) => {
    const typedValues = value.map((v) => {
      return {
        ...v,
        subCodes: sortByDisplayName(v.subCodes),
        type: key
      };
    });

    if (key === 'medicalNeeds') {
      return [
        ...acc,
        {
          ...FAUX_MEDICAL_EQUIPMENT_PSR,
          subCodes: sortByDisplayName(typedValues),
          type: key
        }
      ];
    }

    return [...acc, ...typedValues];
  }, []);

  return sortByDisplayName(combinedPsr);
};
export const selectIsLoading = (state) => selectSlice(state).isLoading;
export const selectErrors = (state) => selectSlice(state).errors;
export const selectHasPsr = (state) => selectSlice(state).hasPsr;
export const selectPsrPreferences = (state) =>
  selectSlice(state).psrPreferences;
export const selectPsrPreferencesArr = (state) => {
  const psrPreferences = selectPsrPreferences(state);

  return Object.entries(psrPreferences).reduce((acc, [key, val]) => {
    const typedValues = val.map((v) => ({ ...v, type: key }));
    return [...acc, ...typedValues];
  }, []);
};
export const selectExpiringPsrPreferences = (state) =>
  selectPsrPreferencesArr(state).filter((pref) => pref.requiresExpiryDate);

export default slice.reducer;
